import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { DTORBIT_URL } from "@/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLUMNS = [
    {
        heading: "Trade / Exchange",
        links: [
            { label: "USDT to INR", to: "/#live-rate", testid: "footer-link-usdt-inr" },
            { label: "Start Exchange", href: DTORBIT_URL, testid: "footer-link-start-exchange" },
            { label: "Exchange Overview", to: "/overview", testid: "footer-link-exchange-overview" },
        ],
    },
    {
        heading: "Features",
        links: [
            { label: "Secure Transactions", to: "/#why", testid: "footer-link-secure" },
            { label: "Competitive Rates", to: "/#live-rate", testid: "footer-link-rates" },
            { label: "Transaction History", to: "/overview#features", testid: "footer-link-history" },
            { label: "Fast Processing", to: "/#how-it-works", testid: "footer-link-fast" },
            { label: "Support", to: "/contact", testid: "footer-link-support" },
        ],
    },
    {
        heading: "Company",
        links: [
            { label: "About Us", to: "/about", testid: "footer-link-about" },
            { label: "Ventures", to: "/ventures", testid: "footer-link-ventures" },
            { label: "Overview", to: "/overview", testid: "footer-link-overview" },
            { label: "Leadership", to: "/leadership", testid: "footer-link-leadership" },
            { label: "Investors", to: "/investors", testid: "footer-link-investors" },
            { label: "Careers", to: "/careers", testid: "footer-link-careers" },
            { label: "Contact Us", to: "/contact", testid: "footer-link-contact" },
        ],
    },
    {
        heading: "Legal / Information",
        links: [
            { label: "Privacy Policy", to: "/privacy", testid: "footer-link-privacy" },
            { label: "Terms of Use", to: "/terms", testid: "footer-link-terms" },
            { label: "Risk Disclosure", to: "/risk-disclosure", testid: "footer-link-risk" },
            { label: "Contact", to: "/contact", testid: "footer-link-legal-contact" },
        ],
    },
];

export default function Footer() {
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState({ state: "idle", message: "" });

    const subscribe = async (e) => {
        e.preventDefault();
        setStatus({ state: "loading", message: "" });
        try {
            const res = await fetch(`${API}/newsletter`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.detail || "Subscription failed");
            setStatus({ state: "success", message: json.message || "Subscribed successfully." });
            setEmail("");
        } catch (err) {
            setStatus({ state: "error", message: err.message || "Something went wrong. Please try again." });
        }
    };

    return (
        <footer
            data-testid="site-footer"
            className="border-t border-[#E4EAF6] bg-white"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-6">
                <div className="lg:col-span-2">
                    <Link
                        to="/"
                        data-testid="footer-logo-link"
                        aria-label="DtOrbit home"
                        className="inline-flex"
                    >
                        <img
                            src="/assets/dtorbit-logo-sm.png"
                            alt="DtOrbit USDT to INR exchange platform logo"
                            className="h-7 w-auto block"
                            width="160"
                            height="40"
                            loading="lazy"
                        />
                    </Link>
                    <p className="mt-5 text-sm text-[#5B6B8C] leading-relaxed max-w-xs">
                        DtOrbit provides a simple and secure platform for USDT to
                        INR exchange — live rates, fast processing and a
                        transparent exchange experience.
                    </p>
                    <form onSubmit={subscribe} className="mt-6" data-testid="newsletter-form">
                        <label
                            htmlFor="newsletter-email"
                            className="block text-xs font-bold text-[#3D4C6E] mb-2"
                        >
                            Subscribe for updates
                        </label>
                        <div className="flex gap-2">
                            <input
                                id="newsletter-email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="newsletter-email-input"
                                placeholder="you@example.com"
                                className="flex-1 min-w-0 rounded-xl border border-[#D5E4FF] bg-white px-4 py-2.5 text-sm text-[#0F1B3D] placeholder:text-[#8494B0] outline-none transition-colors focus:border-[#1E56FF] focus:ring-2 focus:ring-[#1E56FF]/15"
                            />
                            <button
                                type="submit"
                                data-testid="newsletter-subscribe-button"
                                disabled={status.state === "loading"}
                                className="btn-primary !py-2.5 !px-4 text-sm disabled:opacity-60 disabled:pointer-events-none"
                                aria-label="Subscribe to DtOrbit updates"
                            >
                                {status.state === "loading" ? "…" : "Subscribe"}
                            </button>
                        </div>
                        {status.message && (
                            <p
                                data-testid="newsletter-status"
                                className={`mt-2 text-xs font-semibold ${
                                    status.state === "success" ? "text-emerald-600" : "text-rose-500"
                                }`}
                            >
                                {status.message}
                            </p>
                        )}
                    </form>
                </div>

                {COLUMNS.map((column) => (
                    <nav key={column.heading} aria-label={column.heading}>
                        <span className="text-xs uppercase tracking-[0.25em] text-[#1E56FF] font-bold">
                            {column.heading}
                        </span>
                        <ul className="mt-5 flex flex-col gap-3">
                            {column.links.map((link) => (
                                <li key={link.testid}>
                                    {link.href ? (
                                        <a
                                            href={link.href}
                                            data-testid={link.testid}
                                            className="text-sm text-[#3D4C6E] hover:text-[#1E56FF] transition-colors"
                                        >
                                            {link.label}
                                        </a>
                                    ) : (
                                        <Link
                                            to={link.to}
                                            data-testid={link.testid}
                                            className="text-sm text-[#3D4C6E] hover:text-[#1E56FF] transition-colors"
                                        >
                                            {link.label}
                                        </Link>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </nav>
                ))}
            </div>

            <div className="border-t border-[#E4EAF6]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2">
                    <p className="text-xs text-[#8494B0]">
                        © {new Date().getFullYear()} DtOrbit. All Rights Reserved.
                    </p>
                    <p className="text-xs text-[#8494B0]">
                        USDT to INR Exchange · Rates fetched live from dtorbit.com
                    </p>
                </div>
            </div>
        </footer>
    );
}
